import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { RoundTitle } from "./RoundTitle";
import { RoundsTimeline } from "./RoundsTimeline";
import { EarlyBird, EARLY_BIRD_ON } from "./EarlyBird";
import { RoundFeatureCard } from "./RoundFeatureCard";
import { mainRound } from "../hooks/useRounds";
import "./rounds.css";

export const enterPath = (n) => `/events/ultimate-lap/enter/${n}`;

// Warm the sign-up route chunk while the user is still reading the cards so
// the click is instant. Idempotent (webpack caches the promise).
export function prefetchEnterRound() {
  const run = () => import("../pages/EnterRound").catch(() => {});
  if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 1500 });
  else setTimeout(run, 300);
}

/*
  Ultimate Lap microsite - six rounds (client pick: mockup M2, restyled flat).
  Heading "2026-27 Season Championship", then (client, 2026-10-08) the NEXT OPEN
  round as the wide feature card - the same card the landing page uses - then
  the six-node season timeline, then the OTHER five rounds: the first two as
  large cards across the full width, the last three at the standard size.
  Phones: one column, the feature card first, then every round identical.
  The next round's CTA opens /events/ultimate-lap/enter/N; every other round is
  white. No inline entry sheet (client, 2026-09-08).
*/
export default function RoundsGrid({ rounds = [] }) {
  const location = useLocation();
  const sectionRef = useRef(null);
  const didHash = useRef(false);
  const next = mainRound(rounds);
  // Hero slot = the next open round (falls back to the first round that is not
  // done, so the slot never sits empty mid-season).
  const hero = next || rounds.find((x) => x.status !== "completed") || null;
  const rest = hero ? rounds.filter((x) => x.n !== hero.n) : rounds;
  // Timeline node click: bring that round's card into view.
  const focusCard = (n) => {
    document.querySelector(`[data-testid="ul-round-${n}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  useEffect(() => { prefetchEnterRound(); }, []);

  // #rounds hash: land on the section after ScrollToTop has reset the page.
  useEffect(() => {
    if (didHash.current || !rounds.length || location.hash !== "#rounds") return undefined;
    didHash.current = true;
    const t = setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 450);
    return () => clearTimeout(t);
  }, [rounds, location.hash]);

  return (
    <section className="ul-rounds" id="rounds" ref={sectionRef} data-testid="ul-rounds">
      <div className="container-gm">
        <div className="ul-rounds__head">
          <div>
            <h2 className="display-md" data-reveal="mask" data-testid="ul-rounds-heading">2026-27 Season Championship</h2>
          </div>
        </div>

        {EARLY_BIRD_ON && <EarlyBird tone="dark" fee={rounds[0]?.fee_aed} vatPct={rounds[0]?.vat_pct} testid="ul-early-bird" />}

        {hero && (
          <RoundFeatureCard round={hero} tone="dark" testid="ul-hero-card" dataTestidCard={`ul-round-${hero.n}`} extraClass="ul-rounds__hero" />
        )}

        {rounds.length > 0 && (
          <RoundsTimeline rounds={rounds} selected={next?.n ?? null} onSelect={focusCard} testidPrefix="ul-round-node" />
        )}

        <div className={`ul-rounds__grid ${hero ? "ul-rounds__grid--mixed" : ""}`} data-testid="ul-rounds-grid">
          {rest.map((x, idx) => {
            // First two of the remaining rounds take the big slots (client, 2026-10-08).
            const large = !!hero && idx < 2;
            const done = x.status === "completed";
            // Lifecycle (client, 2026-10-20): entries are PER ROUND, so any card
            // here can be open at the same time as the hero round.
            //   done   -> "See results";  open -> yellow "Enter round N";
            //   closed -> "Contact" (opened, then shut);  soon -> "Opening soon".
            // The WHOLE card is the link; the pill is decorative. Rounds with
            // nothing to open are plain cards - no hand, no lift.
            const open = !done && x.entries === "open";
            const closed = !done && x.entries === "closed";
            const to = done ? "/events/ultimate-lap/results" : open ? enterPath(x.n) : closed ? "/contact" : null;
            const label = done ? "See results" : open ? `Enter round ${x.n}` : closed ? "Contact" : "Opening soon";
            const inner = (
              <>
                <div className="ul-round__img">
                  <img src={x.image} alt="" loading="eager" fetchPriority="low" decoding="async" />
                </div>
                <div className="ul-round__body">
                  <RoundTitle round={x} size={large ? "lg" : "md"} testidPrefix={`ul-round-${x.n}`} />
                </div>
                <div className="ul-round__foot">
                  {closed && <p className="ul-round__closed" data-testid={`ul-round-closed-${x.n}`}>Entries closed, please contact management.</p>}
                  <span className={`btn ${open ? "btn--fill" : ""} ${to ? "" : "btn--disabled"} ul-round__pill`} aria-disabled={to ? undefined : "true"} data-testid={`ul-round-cta-${x.n}`}>
                    {label}
                  </span>
                </div>
              </>
            );
            return to ? (
              <Link key={x.n} to={to} className={`ul-round is-${x.status} ${open ? "is-open" : ""} ${large ? "ul-round--lg" : ""}`} aria-label={`Round ${x.n} - ${label}`} data-testid={`ul-round-${x.n}`}>
                {inner}
              </Link>
            ) : (
              <article key={x.n} className={`ul-round is-${x.status} is-static ${large ? "ul-round--lg" : ""}`} data-testid={`ul-round-${x.n}`}>
                {inner}
              </article>
            );
          })}
        </div>

      </div>
    </section>
  );
}
